import { PROSPECT_COMPANIES_QUERY } from "@lib/graphql/outreach";
import { useOutreachListChannel } from "@lib/hooks/useOutreachListChannel";
import { type Connection, usePaginatedList } from "@lib/hooks/usePaginatedList";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { AddBuyersForm } from "./AddBuyersForm";
import type { Buyer, ProspectCompany } from "./types";

export function ProspectsTab() {
  const {
    nodes: companies,
    fetching,
    hasNextPage,
    loadMore,
    refresh,
  } = usePaginatedList<{ prospectCompanies: Connection<ProspectCompany> }, ProspectCompany>({
    query: PROSPECT_COMPANIES_QUERY,
    getConnection: (data) => data.prospectCompanies,
  });
  useOutreachListChannel(refresh);

  if (fetching && companies.length === 0) {
    return <p className="text-sm text-text-muted">Loading prospects…</p>;
  }

  if (companies.length === 0) {
    return (
      <div className="rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
        <p className="text-sm text-text-muted">
          No prospect companies yet. Confirm postings in Triage to create them.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {companies.map((c) => (
        <CompanyRow key={c.id} company={c} onSaved={refresh} />
      ))}
      {hasNextPage && (
        <div className="flex justify-center">
          <button
            onClick={loadMore}
            disabled={fetching}
            className="rounded-md border border-input-border bg-card px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
          >
            {fetching ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}

function CompanyRow({ company, onSaved }: { company: ProspectCompany; onSaved: () => void }) {
  const [expanded, setExpanded] = useState(company.buyerCount === 0);

  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <p className="text-sm font-medium text-text-primary">{company.name}</p>
          <p className="mt-0.5 text-xs text-text-muted">
            {company.sector ? `${company.sector} · ` : ""}
            {company.buyerCount} {company.buyerCount === 1 ? "buyer" : "buyers"}
            {company.hqCountry ? ` · ${company.hqCountry}` : ""}
          </p>
        </div>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3 border-t border-border pt-3">
          {company.buyers.length === 0 ? (
            <p className="text-xs text-text-muted">
              No buyers yet. Paste LinkedIn profile URLs below.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded border border-border">
              {company.buyers.map((b) => (
                <BuyerRow key={b.id} buyer={b} />
              ))}
            </ul>
          )}
          <AddBuyersForm prospectCompanyId={company.id} onSaved={onSaved} />
        </div>
      )}
    </div>
  );
}

function BuyerRow({ buyer }: { buyer: Buyer }) {
  const status = buyer.importStatus;
  const statusClass =
    {
      ready: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
      failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    }[status] ?? "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";

  return (
    <li className="flex items-center justify-between px-3 py-2 text-sm">
      <div>
        <p className="font-medium text-text-primary">{buyer.fullName ?? "Importing…"}</p>
        <p className="text-xs text-text-muted">
          {buyer.title ?? "—"}
          {buyer.linkedinUrl && (
            <>
              {" · "}
              <a
                href={buyer.linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="text-text-muted hover:text-accent"
              >
                LinkedIn
              </a>
            </>
          )}
        </p>
      </div>
      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass}`}>
        {status}
      </span>
    </li>
  );
}
